import React from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { paymentMethods } from '../../../slices/payments';
import Badge from '../../../components/common/Badge';
import AddressSummary from '../../../components/AddressSummary';
import {
  formatGiftCardCode,
  parseGiftCardBalance,
  getCreditCardPaymentInfo,
  priceToString,
} from '../../../utils';

import styles from './PaymentMethodStepInfo.module.scss';
import messages from './messages';
import { STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE } from '../../../utils/common/constants';

import acimaLogo from '../../../assets/images/svg/acima-logo.svg';

const PaymentMethodStepInfo = () => {
  const { formatMessage, formatNumber } = useIntl();

  const imageBase = `${process.env.ASSETS_URL}/images/png`;

  const { selectedPaymentMethod, paymentToken } = useSelector(
    (state) => state.payments,
  );
  const paymentMethodData = paymentToken ? JSON.parse(paymentToken) : null;

  const { giftCards: giftCardsApplied, billingAddress, taxedPrice } = useSelector(
    (state) => state.cart,
  );

  const isStripeRegularCheckoutEnabled = window?.purTogglesEnabled?.(
    STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
  );

  const creditCardPaymentInfo = getCreditCardPaymentInfo({ paymentMethodData });

  return (
    <div className={styles['main-container']} data-private>
      {selectedPaymentMethod === paymentMethods.AFFIRM && (
        <div className={styles['payments-container']}>
          <div className={styles['payment-method-horizontal']}>
            <div className={styles.title}>
              {formatMessage(messages.paymentMethod)}
            </div>
            <div className={styles['payment-method-icon-container']}>
              <img
                alt={formatMessage(messages.affirmLogoAlt)}
                src={`${process.env.ASSETS_URL}/images/svg/affirm-logo.svg`}
                className={styles['affirm-img']}
              />
            </div>
          </div>
        </div>
      )}
      {selectedPaymentMethod === paymentMethods.ACIMA && (
        <div className={styles['payments-container']}>
          <div className={styles['payment-method-horizontal']}>
            <div className={styles.title}>
              {formatMessage(messages.paymentMethod)}
            </div>
            <div className={styles['payment-method-icon-container']}>
              <img
                alt={formatMessage(messages.acimaLogoAlt)}
                src={acimaLogo}
                className={styles['acima-img']}
              />
            </div>
          </div>
        </div>
      )}
      {selectedPaymentMethod === paymentMethods.PAYPAL && (
        <div className={styles['payments-container']}>
          <div className={styles['payment-method-horizontal']}>
            <div className={styles.title}>
              {formatMessage(messages.paymentMethod)}
            </div>
            <div className={styles['payment-method-icon-container']}>
              <img
                alt={formatMessage(messages.payPalLogoAlt)}
                src={`${process.env.ASSETS_URL}/images/svg/paypal-logo.svg`}
                className={styles['paypal-img']}
              />
            </div>
          </div>
        </div>
      )}
      {selectedPaymentMethod === paymentMethods.AMAZON_PAY && (
        <div className={styles['payments-container']}>
          <div className={styles['payment-method-horizontal']}>
            <div className={styles.title}>
              {formatMessage(messages.paymentMethod)}
            </div>
            <div className={styles['payment-method-icon-container']}>
              <img
                alt={formatMessage(messages.amazonPayLogoAlt)}
                src={`${process.env.ASSETS_URL}/images/svg/amazonpay-logo.svg`}
                className={styles['amazon-pay-img']}
              />
            </div>
          </div>
        </div>
      )}
      {selectedPaymentMethod === paymentMethods.APPLE_PAY && (
        <div className={styles['payments-container']}>
          <div className={styles['payment-method-horizontal']}>
            <div className={styles.title}>
              {formatMessage(messages.paymentMethod)}
            </div>
            <div className={styles['payment-method-icon-container']}>
              <img
                alt={formatMessage(messages.applePayLogoAlt)}
                src={`${process.env.ASSETS_URL}/images/svg/apple-pay-logo.svg`}
                className={styles['apple-pay-img']}
              />
            </div>
          </div>
          <div className={styles['billing-address-container']}>
            <div className={styles.title}>
              {formatMessage(messages.billingAddress)}
            </div>
            {billingAddress && (
              <div className={styles['billing-address-data-container']}>
                <AddressSummary
                  phone={billingAddress.phone}
                  firstName={billingAddress.firstName}
                  lastName={billingAddress.lastName}
                  streetAddress={billingAddress.streetName}
                  postalCode={billingAddress.postalCode}
                  city={billingAddress.city}
                  country={billingAddress.country}
                  state={billingAddress.state}
                  showContactInfo={false}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {selectedPaymentMethod === paymentMethods.CREDIT_CARD && paymentToken && (
        <div className={styles['payments-container-cc']}>
          {(!isStripeRegularCheckoutEnabled ||
            creditCardPaymentInfo.type === 'card') && (
              <div className={styles['payment-method']}>
                <div className={styles['title-cc']}>
                  <span className={styles['credit-card-title']}>
                    {formatMessage(messages.paymentMethod)}
                  </span>
                  <span className={styles['credit-card-subtitle']}>
                    {formatMessage(messages.creditCardRowTitle)}
                  </span>
                  <img
                    className={classnames(styles['cc-brand'], styles.hide)}
                    src={`${imageBase}/${creditCardPaymentInfo?.card?.brand?.toLowerCase()}.png`}
                    width="40"
                    height="40"
                    alt={formatMessage(messages.creditCardLogoAlt)}
                  />
                </div>
                <div className={styles['payment-method-data-container']}>
                  <div className={styles['payment-method-data']}>
                    <div>
                      {formatMessage(messages.ending, {
                        last4: creditCardPaymentInfo?.card?.last4,
                      })}
                    </div>
                    <div>
                      {creditCardPaymentInfo?.card?.exp_month}/
                      {creditCardPaymentInfo?.card?.exp_year}
                    </div>
                  </div>
                </div>
              </div>
            )}
          {isStripeRegularCheckoutEnabled &&
            creditCardPaymentInfo.type !== 'card' && (
              <div className={styles['payment-method']}>
                <div className={styles['title-cc']}>
                  <span className={styles['credit-card-title']}>
                    {formatMessage(messages.paymentMethod)}
                  </span>
                  <span className={styles['credit-card-subtitle']}>
                    {creditCardPaymentInfo.prettyType}
                  </span>
                  <img
                    className={classnames(styles['cc-brand'], styles.hide)}
                    src={`${imageBase}/${creditCardPaymentInfo.type}.png`}
                    width="40"
                    height="40"
                    alt={creditCardPaymentInfo.type}
                  />
                </div>
              </div>
            )}
          <div className={styles['billing-address-container']}>
            <div className={styles.title}>
              {formatMessage(messages.billingAddress)}
            </div>
            <div className={styles['billing-address-data-container']}>
              <AddressSummary
                phone={billingAddress?.phone ?? ''}
                firstName={billingAddress?.firstName ?? ''}
                lastName={billingAddress?.lastName ?? ''}
                streetAddress={billingAddress?.streetName ?? ''}
                postalCode={billingAddress?.postalCode ?? ''}
                city={billingAddress?.city ?? ''}
                country={billingAddress?.country ?? ''}
                state={billingAddress?.state ?? ''}
                showContactInfo={false}
              />
            </div>
          </div>
        </div>
      )}
      {giftCardsApplied?.length > 0 && (
        <div
          className={styles['gift-cards-applied-container']}
          data-testid="gift-cards-container"
        >
          <div className={styles.title}>
            {formatMessage(messages.giftCardsApplied)}
          </div>
          {giftCardsApplied.map((giftCardApplied) => (
            <Badge
              text={`${formatGiftCardCode(giftCardApplied.giftCardNumber)}
              - ${parseGiftCardBalance({
                balanceAmount: giftCardApplied.amount,
                formatNumber,
                formatMessage,
              })}`}
              key={giftCardApplied.giftCardNumber}
              isReadOnly
            />
          ))}
        </div>
      )}
      {isStripeRegularCheckoutEnabled && taxedPrice && (
        <div className={styles['total-price-container']}>
          <span className={styles.title}>{formatMessage(messages.totalPrice)}</span>
          <span
            className={
              styles['payment-method-data']
            }
          >
            {priceToString({
              price: {
                centAmount:
                  taxedPrice?.totalGross?.centAmount ?? 0,
                currencyCode: 'USD',
                fractionDigits: 2
              },
              forceDigit: true,
              formatNumber,
              formatMessage,
            })}
          </span>
        </div>
      )}
    </div>
  );
};
export default PaymentMethodStepInfo;
